import { FC, ReactElement, useMemo, useState } from 'react';

import { BrandLinkStatusOptions, BrandLinkStatuses } from '@constants/AccountConstants';
import { keyBy, orderBy } from 'lodash';
import moment from 'moment';

import { AccountLink, DO_NOT_HAVE_PERMISSIONS_MESSAGE } from '@sparkplug/lib';

import TagAssistCallout from '@features/account-links/components/TagAssistCallout';
import TagAssistDisableModal from '@features/account-links/components/TagAssistDisableModal';
import TagAssistEnableModal from '@features/account-links/components/TagAssistEnableModal';
import { useAccountTagCounts } from '@features/account-links/queries';
import {
    addAccountLinkSearchableFields,
    filterByTagStatus,
    isLinkMapped,
    isLinkPending,
} from '@features/account-links/utils';

import { useAdminAccountUpdate } from '@contexts/AccountContext/AccountAdminMutations';

import Button from '@components/buttons/Button';
import Form from '@components/form/Form';
import { NoRetailersFoundGraphic } from '@components/graphics';
import { CircleCheck, CircleXIcon, GeneralSettingsIcon, ResetIcon } from '@components/icons';
import EmptyStateDisplay from '@components/layout/EmptyStateDisplay';
import Tooltip from '@components/layout/Tooltip';
import { useHistory } from '@components/router';
import Table from '@components/table/Table';
import toast from '@components/toast';
import Toolbar from '@components/toolbar/Toolbar';

import { useApp } from '@hooks/AppHooks';
import { useQueryParamsState } from '@hooks/QueryParamsHooks';
import { useSparkplugAccount, useTotalProductTagCount } from '@hooks/SparkplugAccountsHooks';
import { useSearch } from '@hooks/UIHooks';

import { isEmpty } from '@helpers/util';

import { ITableRow, THeadCell } from '@app/types/TableTypes';

import { useRespondToAccountLinkRequest } from '../../../mutations';
import AccountLinkLabel from './components/AccountLinkLabel';
import AccountLinkMappedBrands from './components/AccountLinkMappedBrands';
import AccountStatusChip from './components/AccountStatusChip';
import TagStatus from './components/TagStatus';

import './LinkedAccountsTab.scss';

export interface LinkedAccountsTabProps {
    accountLinksAreReady: boolean;
    accountLinks: AccountLink[];
    toolbarMarkets: ReactElement;
}

const LinkedAccountsTab: FC<LinkedAccountsTabProps> = ({
    accountLinksAreReady,
    accountLinks,
    toolbarMarkets,
}) => {
    const [showEnableTagAssistantModal, setShowEnableTagAssistantModal] = useState<boolean>(false);
    const [showDisableTagAssistantModal, setShowDisableTagAssistantModal] =
        useState<boolean>(false);
    const { appIsMobile } = useApp();
    const { account, userCan } = useSparkplugAccount();
    const userCanManageLinks = userCan('manageLinks');
    const userCanManageProductTags = userCan('manageProductTags');

    const { accountTagCountsAreReady, accountTagCountsByBrandLinkId } = useAccountTagCounts({
        accountId: account?._id ?? '',
        isEnabled: account?.type === 'brand',
    });

    const { totalProductTagCountIsReady, totalProductTagCount } = useTotalProductTagCount({
        accountId: account?._id ?? '',
        isEnabled: account?.type === 'brand',
    });

    const { refetchAccount } = useSparkplugAccount();
    const { adminUpdateAccountAsync } = useAdminAccountUpdate(account?._id ?? '');

    const sortedAccountLinks = useMemo(() => {
        const sortedLinks = orderBy(
            accountLinks,
            [(links) => links?.accountName.toLowerCase()],
            ['asc'],
        );

        if (account?.type === 'retailer') {
            const { requested, pending, other } = sortedLinks.reduce<{
                requested: AccountLink[];
                pending: AccountLink[];
                other: AccountLink[];
            }>(
                (res, accountLink) => {
                    if (accountLink.status === 'pending') {
                        res.requested.push(accountLink);
                    } else if (isLinkPending(accountLink)) {
                        res.pending.push(accountLink);
                    } else {
                        res.other.push(accountLink);
                    }
                    return res;
                },
                { requested: [], pending: [], other: [] },
            );
            return [...pending, ...requested, ...other];
        }

        return sortedLinks.map((accountLink) => {
            return {
                ...accountLink,
                brandLinks: accountLink.brandLinks?.map((brandLink) => ({
                    ...brandLink,
                    ...(brandLink._id
                        ? accountTagCountsByBrandLinkId?.[brandLink?._id ?? ''] ?? {}
                        : {}),
                })),
            };
        });
    }, [accountLinks, accountTagCountsByBrandLinkId]);

    // This lets us search the `accountLinks` array by brand name, or maybe other keys in the future...?
    const searchableAccountLinks = useMemo(() => {
        return addAccountLinkSearchableFields(sortedAccountLinks);
    }, [sortedAccountLinks]);

    const history = useHistory();

    const [query, setQuery] = useQueryParamsState<{
        tagStatusFilter: 'missingTags' | 'allTagged' | 'nonMapped' | 'all';
        statusFilter: 'all' | 'pending' | 'disabled' | 'rejected' | 'enabled';
    }>({ tagStatusFilter: 'all', statusFilter: 'all' });
    const { tagStatusFilter, statusFilter } = query;

    const setTagStatusFilter = (
        updatedTagStatusFilter: 'missingTags' | 'allTagged' | 'nonMapped' | 'all',
    ) => setQuery({ tagStatusFilter: updatedTagStatusFilter });

    const setStatusFilter = (
        updatedStatusFilter: 'all' | 'pending' | 'disabled' | 'rejected' | 'enabled',
    ) => setQuery({ statusFilter: updatedStatusFilter });

    const { searchFilter, onChangeSearchFilter, applySearch } = useSearch([
        'accountName',
        'mappedNamesStr',
    ]);

    const { isSendingAccountLinkRequest, respondToAccountLinkRequestAsync } =
        useRespondToAccountLinkRequest(account?._id || '');

    const handleResponse = async (vendorAccountId: string, accepted: boolean, updateRow: any) => {
        toast.promise(respondToAccountLinkRequestAsync({ vendorAccountId, accepted }), {
            loading: `${accepted ? 'Accepting' : 'Declining'} request...`,
            success: () => {
                if (accepted) {
                    updateRow((row: ITableRow<AccountLink>) => {
                        return {
                            ...row,
                            status: 'enabled',
                        } as any;
                    });
                    history.push(
                        `/${account?._id}/settings/vendor/${vendorAccountId}/manage?isNewVendorLink=true`,
                    );
                } else {
                    updateRow((row: ITableRow<AccountLink>) => {
                        return {
                            ...row,
                            status: 'rejected',
                        } as any;
                    });
                }
                return `Vendor link request ${accepted ? 'accepted' : 'declined'}`;
            },
            error: 'Something went wrong - please try again and contact support if this issue persists.',
        });
    };

    const generateActionRow = (row: ITableRow<AccountLink & { key: string }>, updateRow: any) => {
        let actionElement: ReactElement = <></>;

        if (account?.type === 'retailer') {
            switch (row.status) {
                case 'pending':
                    actionElement = (
                        <div className="actions-wrapper">
                            <Button
                                disabled={isSendingAccountLinkRequest || !userCanManageLinks}
                                tooltip={
                                    !userCanManageLinks
                                        ? DO_NOT_HAVE_PERMISSIONS_MESSAGE
                                        : undefined
                                }
                                variant="smooth"
                                color="red"
                                onClick={() => handleResponse(row.accountId, false, updateRow)}
                            >
                                {appIsMobile ? <CircleXIcon className="decline" /> : 'Decline'}
                            </Button>
                            <Button
                                disabled={isSendingAccountLinkRequest || !userCanManageLinks}
                                tooltip={
                                    !userCanManageLinks
                                        ? DO_NOT_HAVE_PERMISSIONS_MESSAGE
                                        : undefined
                                }
                                variant="smooth"
                                color="green"
                                onClick={() => handleResponse(row.accountId, true, updateRow)}
                            >
                                {appIsMobile ? <CircleCheck className="accept" /> : 'Accept'}
                            </Button>
                        </div>
                    );
                    break;
                case 'rejected':
                    actionElement = (
                        <Tooltip
                            title={
                                !userCanManageLinks
                                    ? DO_NOT_HAVE_PERMISSIONS_MESSAGE
                                    : 'Reopen link request'
                            }
                        >
                            <ResetIcon
                                className="reset-icon"
                                onClick={() => {
                                    if (!userCanManageLinks) return;
                                    updateRow((prevRow: any) => {
                                        return {
                                            ...prevRow,
                                            status: 'pending',
                                        } as any;
                                    });
                                }}
                            />
                        </Tooltip>
                    );
                    break;
                default:
                    actionElement = (
                        <Button
                            startIcon={<GeneralSettingsIcon />}
                            onClick={() =>
                                history.push(
                                    `/${account?._id}/settings/vendor/${row.accountId}/manage`,
                                )
                            }
                            variant="outlined"
                            color="blue"
                            disabled={!userCanManageLinks}
                            tooltip={
                                !userCanManageLinks ? DO_NOT_HAVE_PERMISSIONS_MESSAGE : undefined
                            }
                        >
                            {appIsMobile ? undefined : 'Manage'}
                        </Button>
                    );
                    break;
            }
        }
        if (account?.type === 'brand' && row.status === 'enabled' && !isLinkPending(row)) {
            actionElement = (
                <Button
                    startIcon={<GeneralSettingsIcon />}
                    onClick={() =>
                        history.push(`/${account._id}/settings/product-tags/${row.accountId}`)
                    }
                    variant="outlined"
                    color="blue"
                    disabled={!userCanManageLinks}
                    tooltip={!userCanManageLinks ? DO_NOT_HAVE_PERMISSIONS_MESSAGE : undefined}
                >
                    {appIsMobile ? undefined : 'Manage'}
                </Button>
            );
        }

        return <Table.Cell align="right">{actionElement}</Table.Cell>;
    };

    const headCells: THeadCell<
        AccountLink & {
            key: string;
            accountLinkStatus?: BrandLinkStatusOptions;
        }
    >[] = useMemo(() => {
        return [
            {
                id: 'accountName',
                sortType: 'string',
                label: account?.type === 'retailer' ? 'Vendor' : 'Retailer',
                render: (row) => (
                    <Table.Cell align="left">
                        <AccountLinkLabel accountLink={row} />
                    </Table.Cell>
                ),
            },
            {
                id: 'mappedNamesStr',
                sortType: 'string',
                label: 'Mapped Brands',
                render: (row) => (
                    <Table.Cell align="left">
                        <AccountLinkMappedBrands accountLink={row} />
                    </Table.Cell>
                ),
                isHidden: appIsMobile,
            },
            {
                id: 'accountLinkStatus.label',
                sortType: 'string',
                label: 'Link Status',
                render: (row) => (
                    <Table.Cell align="left">
                        <AccountStatusChip accountLink={row} accountType={account?.type} />
                    </Table.Cell>
                ),
            },
            {
                id: 'tagStatus',
                sortType: 'string',
                label: 'Tag Status',
                isHidden: account?.type !== 'brand',
                render: (row) => (
                    <Table.Cell align="left">
                        <TagStatus accountTagCountsAreReady={accountTagCountsAreReady} row={row} />
                    </Table.Cell>
                ),
            },
            {
                id: 'actions',
                render: generateActionRow,
            },
        ];
    }, [accountTagCountsAreReady, account, appIsMobile]);

    const genAccountLinkStatus = (accountLink: AccountLink) => {
        const brandLinkStatusesByValue = keyBy(BrandLinkStatuses, 'value');
        if (isLinkMapped(accountLink)) {
            return brandLinkStatusesByValue.unmapped;
        }
        if (isLinkPending(accountLink)) {
            return brandLinkStatusesByValue['pending-assignment'];
        }
        return brandLinkStatusesByValue[accountLink.status];
    };

    const applyTagStatusFilter = (list: ITableRow<AccountLink>[]) =>
        filterByTagStatus(tagStatusFilter, list);

    const applyStatusFilter = (list: ITableRow<AccountLink>[]) =>
        list.filter((accountLink) => {
            if (statusFilter === 'all') {
                return true;
            }
            if (statusFilter === 'unmapped') {
                return isLinkMapped(accountLink);
            }
            return accountLink.status === statusFilter;
        });

    const enableAssistant = () => {
        toast.promise(
            adminUpdateAccountAsync(
                {
                    tagAssistantEnabled: true,
                },
                {
                    onSuccess: () => {
                        refetchAccount();
                    },
                    onSettled: () => {
                        setShowEnableTagAssistantModal(false);
                    },
                },
            ),
            {
                loading: 'Enabling Tag Assistant...',
                success: 'Tag Assistant enabled',
                error: 'Something went wrong - please try again and contact support if this issue persists.',
            },
        );
    };

    const { TagAssistantInfo, TagAssistantToggleSwitch } = useMemo(() => {
        const hasBeenMoreThanFiveHours = account?.tagAssistantEnabledAt
            ? moment(account?.tagAssistantEnabledAt).add(5, 'hours').isBefore(moment().utc())
            : false;

        const sharedConditions =
            account?.type === 'brand' && totalProductTagCountIsReady && totalProductTagCount! > 0;

        const shouldShowAssistantCallout =
            sharedConditions &&
            !account?.tagAssistantMessageDismissed &&
            !hasBeenMoreThanFiveHours &&
            userCanManageLinks;

        const TagAssistantInfoElement = shouldShowAssistantCallout ? (
            <TagAssistCallout
                showModal={() => setShowEnableTagAssistantModal(true)}
                autoplayAnimation
                accountId={account?._id}
                tagAssistantEnabledAt={account?.tagAssistantEnabledAt}
            />
        ) : (
            <></>
        );

        const shouldShowToggle =
            sharedConditions && (hasBeenMoreThanFiveHours || account?.tagAssistantMessageDismissed);

        const TagAssistantToggleSwitchElement = shouldShowToggle ? (
            <Form.Switch
                label={
                    <span
                        className={`tag-assistant-switch ${
                            !account?.tagAssistantEnabled ? 'disabled' : ''
                        }`}
                    >
                        Tag Assistant
                    </span>
                }
                value={Boolean(account?.tagAssistantEnabled)}
                color="purple"
                showIcon
                disabled={!userCanManageProductTags}
                tooltip={!userCanManageProductTags ? DO_NOT_HAVE_PERMISSIONS_MESSAGE : undefined}
                onChange={() => {
                    if (account?.tagAssistantEnabled) {
                        setShowDisableTagAssistantModal(true);
                    } else {
                        setShowEnableTagAssistantModal(true);
                    }
                }}
            />
        ) : (
            <></>
        );

        return {
            TagAssistantInfo: TagAssistantInfoElement,
            TagAssistantToggleSwitch: TagAssistantToggleSwitchElement,
        };
    }, [
        account?.type,
        account?.tagAssistantMessageDismissed,
        account?.tagAssistantEnabledAt,
        totalProductTagCount,
        totalProductTagCountIsReady,
    ]);

    return (
        <>
            {TagAssistantInfo}
            <div className="account-links-header">
                <Toolbar className="account-links-toolbar">
                    <Toolbar.Search
                        name="search"
                        defaultValue={searchFilter}
                        onChange={onChangeSearchFilter}
                    />
                    <Toolbar.Text className="account-markets toolbar-group-start">
                        {toolbarMarkets}
                    </Toolbar.Text>
                    <Toolbar.Dropdown
                        className="status-dropdown"
                        label={null}
                        options={[
                            { label: 'All Statuses', value: 'all' },
                            { label: 'Requested', value: 'pending' },
                            { label: 'Disabled', value: 'disabled' },
                            { label: 'Rejected', value: 'rejected' },
                            { label: 'Enabled', value: 'enabled' },
                            { label: 'Unmapped', value: 'unmapped' },
                        ]}
                        value={statusFilter}
                        onSelect={(value) => setStatusFilter(value)}
                    />
                    {account?.type === 'brand' && (
                        <Toolbar.Dropdown
                            className="tag-status-dropdown"
                            label={null}
                            options={[
                                { label: 'All Tag Statuses', value: 'all' },
                                { label: 'Products Missing Tags', value: 'missingTags' },
                                { label: 'All Products Tagged', value: 'allTagged' },
                                { label: 'No Products Mapped', value: 'nonMapped' },
                            ]}
                            value={tagStatusFilter}
                            onSelect={(value) => setTagStatusFilter(value)}
                        />
                    )}
                </Toolbar>
                {TagAssistantToggleSwitch}
                <TagAssistDisableModal
                    accountId={account?._id}
                    isVisible={showDisableTagAssistantModal}
                    onClose={() => setShowDisableTagAssistantModal(false)}
                />
                <TagAssistEnableModal
                    onClose={() => setShowEnableTagAssistantModal(false)}
                    onConfirm={enableAssistant}
                    isVisible={showEnableTagAssistantModal}
                />
            </div>

            {!isEmpty(searchableAccountLinks) || !accountLinksAreReady ? (
                <Table
                    className="account-links-table"
                    isLoading={!accountLinksAreReady}
                    variant="raised"
                    rows={searchableAccountLinks.map((accountLink) => ({
                        ...accountLink,
                        accountLinkStatus: genAccountLinkStatus(accountLink),
                        key: [
                            accountLink.accountId,
                            accountLink.status,
                            accountTagCountsAreReady,
                        ].join('-'),
                    }))}
                    headCells={headCells}
                    defaultOptions={{}}
                    filters={[applySearch, applyTagStatusFilter, applyStatusFilter]}
                    enableQueryParams
                >
                    <Table.RenderHead />
                    <Table.RenderBody />
                </Table>
            ) : (
                <EmptyStateDisplay
                    className="admin-brand-links-empty-state"
                    graphic={<NoRetailersFoundGraphic />}
                    label={
                        account?.type === 'brand' ? 'No Retailers Found' : 'No Vendor Links Found'
                    }
                />
            )}
        </>
    );
};

export default LinkedAccountsTab;
